import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  TRACKING_ID_KEY,
  readTrackingId,
  writeTrackingId,
  clearTrackingId,
  isTrackingId,
} from "~/utils/trackingId";
import type { Organisation, Address, CostCentre } from "~/types";
import { extractErrorMessage, formatHttpError, handleInvalidToken } from "~/utils/apiError";

// --------------- Interfaces ---------------

export interface AuthToken {
  token: string;
  role?: string;
  organisation?: Organisation | null;
  /** Hub user behind a "Login as Customer" session; only read off the `?loginAs=` payload. */
  tracking_id?: string | null;
}

export interface User {
  id: number | string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  business_name?: string;
  phone?: string;
  addresses?: Address[];
  organisation_id?: number | string | null;
  email_delivery_day_reminder?: boolean;
  email_order_open_notification?: boolean;
  email_order_closing_tonight?: boolean;
  email_post_delivery_review?: boolean;
}

export type { Address };

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface PendingOrgCustomerLookup {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  auto_activate?: boolean;
  role?: string;
  business_name?: string;
  organisation?: {
    name?: string;
    delivery_address?: {
      line_1?: string;
      line_2?: string;
      city?: string;
      zip?: string;
    };
  };
}

// The backend rejects a password or Google login on an unverified account with the
// same string, so both answer it with the verify instructions rather than the raw
// server string. Only password login points at LoginForm's resend button: after a
// Google sign-in there is no typed email for it to send. The SSO exchange returns
// the same 403, but callback.vue maps it itself.
function unverifiedAccountMessage(error: unknown, canResend: boolean): string | null {
  return extractErrorMessage(error) === "Customer account not activated"
    ? `Your account needs to be verified. Please check your email for the verification link${canResend ? ", or request a new one below" : ""}.`
    : null;
}

export const useAuthStore = defineStore("auth", () => {
  const config = useRuntimeConfig();
  const { identify: trackIdentify, reset: trackReset, track, syncStaffAssisted } = useTracking();

  // --------------- State ---------------
  const user = ref<User | null>(null);
  const token = ref<AuthToken | null>(null);
  const organisation = ref<Organisation | null>(null);
  const customerRole = ref<string | null>(null);
  const authLoading = ref(false);
  const accountLoading = ref(false);
  const loginError = ref<string | null>(null);
  const loginNeedsVerification = ref(false);
  const signUpError = ref<string | null>(null);
  const signUpSuccess = ref(false);
  const verifySuccess = ref(false);
  const verifyError = ref<string | null>(null);
  const verifiedEmail = ref<string | null>(null);
  const verificationType = ref<string | null>(null);
  const resetConfirmation = ref(false);
  const resetPasswordError = ref<string | null>(null);
  const resetPasswordSuccess = ref(false);
  const newVerificationSuccess = ref(false);
  const newVerificationError = ref<string | false>(false);
  const customerCostCentres = ref<CostCentre[]>([]);
  // Mirrors the localStorage key the `$fetch` interceptor reads, so the staff-mode notice reacts.
  const trackingId = ref<string | null>(null);
  // Another tab can start or end staff mode on this browser; follow it so this tab's notice matches
  // what its calls send. No logout here: the other tab may have just written its own session.
  if (import.meta.client) {
    window.addEventListener("storage", (event) => {
      if (event.key !== TRACKING_ID_KEY && event.key !== null) return;
      trackingId.value = readTrackingId();
      syncStaffAssisted();
    });
  }

  // `organisation` is `Organisation | null`, so `null` alone cannot distinguish
  // "not fetched yet" from "this customer has no organisation" from "the fetch
  // failed" — getOrganisation swallows every error that isn't a literal
  // "Invalid Token" body. Consumers that must act on a *denial* (e.g. resetting
  // an account-billed cart back to card) need to know the difference: acting on
  // an unresolved org silently downgrades legitimate orders.
  //
  // authInitialised: initializeAuth has run to completion. Stays false during
  // SSR (initializeAuth bails on !import.meta.client), which is what makes
  // `!isLoggedIn` safe to treat as a definitive answer — before this flag flips,
  // token is always null and every customer looks logged out.
  const authInitialised = ref(false);
  // organisationResolved: we hold a definitive answer about this customer's
  // organisation — either it was fetched, or the profile told us there is none.
  // Deliberately stays false when getOrganisation fails.
  const organisationResolved = ref(false);
  const organisationMembersResolved = ref(false);
  const organisationMembersLoading = ref(false);

  // --------------- Getters ---------------

  const isLoggedIn = computed(() => !!token.value?.token);
  const authenticated = computed(() => !!token.value?.token);

  const isPremiumCustomer = computed(() => {
    const sub = organisation.value?.subscription;
    return !!sub && !!sub.subscription_type_id;
  });

  // --------------- Actions ---------------

  /**
   * Shared success tail for the three credential flows (login / googleAuth /
   * ssoAuth): persist the token, fire the analytics event, upgrade org users'
   * default landing to /orders, recalc the cart with org pricing, then load the
   * customer (which owns the redirect navigation). The failure branch is
   * deliberately NOT here — it differs per caller (login/googleAuth set
   * loginError; ssoAuth throws — see ssoAuth doc).
   */
  async function finaliseAuth(
    response: AuthToken,
    redirect: string | undefined,
    method: "email" | "google" | "sso",
    attempt: number,
  ): Promise<void> {
    // Bail only when a newer attempt has already logged in — otherwise a later
    // typo would throw away a login that worked. Ticket 1143.
    if (attempt !== loginAttemptSeq && isLoggedIn.value) return;

    // Read before setAuthenticated flips it. Two overlapping attempts can both
    // reach here — the superseded one is allowed to establish the session when
    // nobody is logged in yet (see the guard above) — but the customer signed in
    // once, so only the attempt that makes the logged-out -> logged-in
    // transition reports it. Raised by Sean on #211.
    const establishedSession = !isLoggedIn.value;

    setAuthenticated(response);
    // A customer signing in themselves ends staff mode on this browser.
    setTrackingId(null);

    if (establishedSession) {
      track("login_completed", { method });
    }

    let target = redirect;
    if (response.organisation) {
      organisation.value = response.organisation;
      organisationResolved.value = true;
      if (!target || target === "/search") {
        target = "/orders";
      }
    }

    // Recalculate cart with org-specific pricing
    const ordersStore = useOrdersStore();
    ordersStore.recalculateCart();

    await getCustomer(target ?? "/search", attempt);
  }

  /**
   * Hydrate auth state from localStorage on app boot.
   * Mirrors old-site/plugins/auth-init.js logic.
   */
  async function initializeAuth(): Promise<void> {
    if (!import.meta.client) return;

    // Every client-side exit path below marks auth as initialised, so consumers
    // can tell "definitively logged out" from "not booted yet". The SSR bail
    // above is deliberately outside this — nothing has been hydrated there.
    try {
      trackingId.value = readTrackingId();
      consumeAuthHandoffFragment();

      const route = useRoute();

      // Support ?loginAs= query param (admin impersonation)
      if (route.query.loginAs) {
        try {
          const parsed: AuthToken = JSON.parse(route.query.loginAs as string);
          setAuthenticated(parsed);
          await Promise.all([getCustomer(), getOrganisation()]);
          // Only once the token has resolved a customer: a crafted link must not plant staff mode.
          if (token.value && user.value) {
            setTrackingId(isTrackingId(parsed.tracking_id) ? parsed.tracking_id : null);
          }
          const ordersStore = useOrdersStore();
          ordersStore.recalculateCart();
          return;
        } catch (e) {
          console.error("Invalid loginAs query param:", e);
        }
      }

      const stored = localStorage.getItem("authenticated");

      if (!stored || stored === "false") {
        token.value = null;
        return;
      }

      try {
        const parsed: AuthToken = JSON.parse(stored);
        if (parsed && typeof parsed === "object" && parsed.token) {
          setAuthenticated(parsed);

          const promises: Promise<void>[] = [getCustomer()];
          if (parsed.role && parsed.role !== "customer") {
            promises.push(getOrganisation());
          }
          await Promise.all(promises);

          const ordersStore = useOrdersStore();
          ordersStore.recalculateCart();
        } else {
          token.value = null;
        }
      } catch {
        token.value = null;
        localStorage.removeItem("authenticated");
      }
    } finally {
      authInitialised.value = true;
    }
  }

  // Bumped by every authentication attempt, not just login() — a slow email
  // login that resolves after a Google sign-in would otherwise overwrite it.
  // See the guards in login() (ticket 1143).
  let loginAttemptSeq = 0;

  async function login(credentials: LoginCredentials, redirect?: string): Promise<void> {
    const attempt = ++loginAttemptSeq;

    authLoading.value = true;
    loginError.value = null;
    loginNeedsVerification.value = false;

    try {
      const response = await $fetch<AuthToken>(
        `${config.public.apiBase}/api/public/customers/login`,
        {
          method: "POST",
          body: credentials,
        },
      );

      await finaliseAuth(response, redirect, "email", attempt);
    } catch (error: unknown) {
      // Superseded — the newest attempt owns the banner and the loading flag.
      if (attempt !== loginAttemptSeq) return;

      const unverified = unverifiedAccountMessage(error, true);
      loginNeedsVerification.value = unverified !== null;
      loginError.value =
        unverified ?? formatHttpError(error, "We couldn't sign you in. Please try again.");
      // Deliberately does not clear the token — a rejected login never set one,
      // and a token another attempt established is not ours to destroy. Ticket 1143.
      authLoading.value = false;
    }
  }

  async function googleAuth(payload: {
    token: { idToken: string };
    redirect?: string;
  }): Promise<void> {
    // Same sequence as login(), and the same guards both ways: a password attempt
    // still in flight knows it has been superseded, and so does this one.
    const attempt = ++loginAttemptSeq;

    authLoading.value = true;
    loginError.value = null;
    loginNeedsVerification.value = false;

    try {
      const response = await $fetch<AuthToken>(`${config.public.apiBase}/api/auth/google`, {
        method: "POST",
        body: payload.token,
      });

      await finaliseAuth(response, payload.redirect, "google", attempt);
    } catch (error: unknown) {
      // Superseded — the newest attempt owns the banner and the loading flag.
      if (attempt !== loginAttemptSeq) return;

      loginError.value =
        unverifiedAccountMessage(error, false) ??
        formatHttpError(
          error,
          "We couldn't sign you in with Google. Please try again or use another method.",
        );
      authLoading.value = false;
    }
  }

  /**
   * Redeem the one-time SSO login code minted by the backend callback
   * (POST /api/public/auth/exchange — code is single-use, 60s TTL). Response is
   * the same AuthToken shape as login/googleAuth. Unlike those, this THROWS
   * on failure instead of setting loginError: the caller is /auth/callback,
   * not the login form, and the page needs the raw error (401 = expired or
   * already-redeemed code) to pick its own copy.
   */
  async function ssoAuth(payload: { code: string; redirect?: string }): Promise<void> {
    // Cleared even though this function never SETS loginError: a failed
    // password/Google attempt leaves it populated, and LoginForm renders it
    // unconditionally, so an unrelated stale error would greet the user on the
    // bounce back to /login after an SSO failure.
    loginError.value = null;
    loginNeedsVerification.value = false;
    const attempt = ++loginAttemptSeq;

    const response = await $fetch<AuthToken>(`${config.public.apiBase}/api/public/auth/exchange`, {
      method: "POST",
      body: { code: payload.code },
    });

    await finaliseAuth(response, payload.redirect, "sso", attempt);
  }

  /**
   * Email-first SSO entry: does this email's domain have SSO? Unauthenticated, so a
   * plain $fetch. The connection id stays on the backend — SsoButton does a full-page
   * redirect into /authorize?email=..., which resolves it again server-side.
   */
  async function lookupSsoConnection(email: string): Promise<boolean> {
    return await $fetch<boolean>(`${config.public.apiBase}/api/public/auth/lookup`, {
      method: "POST",
      body: { email },
    });
  }

  async function register(payload: Record<string, unknown>): Promise<void> {
    signUpError.value = null;

    try {
      const response = await $fetch<AuthToken>(
        `${config.public.apiBase}/api/public/customers/register`,
        {
          method: "POST",
          body: payload,
        },
      );

      signUpSuccess.value = true;
      signUpError.value = null;
      track("signup_completed", { method: "email" });

      if (response.organisation) {
        setAuthenticated(response);
        setTrackingId(null);
        await getCustomer("/orders");
      }
    } catch (error: unknown) {
      signUpSuccess.value = false;
      signUpError.value = formatHttpError(
        error,
        "We couldn't create your account. Please try again.",
      );
      token.value = null;
      throw error;
    }
  }

  /**
   * Upgrade a guest customer record (created during a guest checkout) into
   * a fully active account. Hits the new public `convert-guest` endpoint
   * (no JWT — email + password are the credentials). Past guest orders
   * with the same email are linked server-side, so the legacy `linked_order`
   * payload is intentionally absent.
   */
  async function convertGuestToCustomer(payload: Record<string, unknown>): Promise<void> {
    signUpError.value = null;

    try {
      const response = await $fetch<AuthToken>(
        `${config.public.apiBase}/api/public/customers/convert-guest`,
        { method: "POST", body: payload },
      );

      signUpSuccess.value = true;
      signUpError.value = null;
      track("signup_completed", { method: "convert_guest" });

      if (response?.token) {
        setAuthenticated(response);
        setTrackingId(null);
        await getCustomer("/orders");
      }
    } catch (error: unknown) {
      signUpSuccess.value = false;
      signUpError.value = formatHttpError(
        error,
        "We couldn't upgrade your guest checkout to a full account. Please try again.",
      );
      throw new Error("Failed to convert guest customer", { cause: error });
    }
  }

  /**
   * True when `attempt` belongs to a login that a newer one has already replaced.
   *
   * Untagged callers (`initializeAuth`, `register`, guest conversion, `updateCustomer`)
   * pass nothing and are never treated as superseded — they are not racing a login.
   */
  function isSupersededAttempt(attempt?: number): boolean {
    return attempt !== undefined && attempt !== loginAttemptSeq;
  }

  async function getCustomer(redirect?: string, attempt?: number): Promise<void> {
    if (!token.value?.token) return;

    accountLoading.value = true;

    try {
      const response = await $fetch<User & { costCentres?: CostCentre[] }>(
        `${config.public.apiBase}/api/customers/profile`,
        { headers: _authHeaders() },
      );

      // This profile belongs to a login that has since been superseded: writing it
      // would put one customer's details behind another's token and identify the
      // wrong person to Mixpanel. Leave `accountLoading` alone — the newest attempt
      // owns it, same as `authLoading`. Raised by Sean and Copilot on #211.
      if (isSupersededAttempt(attempt)) return;

      user.value = response;
      // A profile with no organisation_id is a definitive "this customer has no
      // organisation" — there is nothing left to fetch, so account billing is
      // resolved as unavailable. Decided here, where the payload is in hand,
      // rather than by consumers reading user.organisation_id: a consumer cannot
      // tell a genuinely absent org from a fetch that hasn't returned yet.
      // Never set false here — an org customer's answer comes from
      // getOrganisation, which may still be in flight.
      if (!response.organisation_id) {
        organisationResolved.value = true;
      }
      setCustomerCostCentres(response.costCentres || []);
      loginError.value = null;
      loginNeedsVerification.value = false;
      authLoading.value = false;
      accountLoading.value = false;

      // Identify user in Mixpanel
      trackIdentify(response.id, {
        $email: response.email,
        $first_name: response.first_name,
        $last_name: response.last_name,
        role: response.role,
      });

      if (redirect) {
        navigateTo(redirect);
      }
    } catch (error: unknown) {
      // Same reasoning as the success path, and this is the sharp edge: the clear
      // below is unconditional, so without this a network blip on a superseded
      // attempt's profile fetch wipes the session a newer login just established.
      if (isSupersededAttempt(attempt)) return;

      token.value = null;
      authLoading.value = false;
      accountLoading.value = false;
      handleInvalidToken(error);
    }
  }

  async function updateCustomer(payload: Partial<User>): Promise<boolean> {
    if (!token.value?.token) return false;

    accountLoading.value = true;
    try {
      await $fetch(`${config.public.apiBase}/api/customers`, {
        method: "PUT",
        headers: _authHeaders(),
        body: payload,
      });

      signUpError.value = null;
      await getCustomer("/account/details");
      return true;
    } catch (error: unknown) {
      signUpError.value = formatHttpError(
        error,
        "We couldn't save your changes. Please try again.",
      );
      handleInvalidToken(error);
      return false;
    } finally {
      accountLoading.value = false;
    }
  }

  async function getOrgCustomer(email: string): Promise<PendingOrgCustomerLookup | null> {
    try {
      return await $fetch<PendingOrgCustomerLookup>(
        `${config.public.apiBase}/api/public/organisations/getPendingOrgCustomerByEmail`,
        { params: { email } },
      );
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw error;
    }
  }

  function setOrganisation(fresh: Organisation): void {
    organisation.value = fresh;
    organisationResolved.value = true;
    organisationMembersResolved.value = true;
  }

  async function getOrganisation(): Promise<void> {
    if (!token.value?.token) return;

    organisationMembersLoading.value = true;
    try {
      const response = await $fetch<Organisation>(
        `${config.public.apiBase}/api/customers/organisations`,
        { headers: _authHeaders() },
      );

      setOrganisation(response);
    } catch (error: unknown) {
      // Deliberately leaves organisationResolved false. handleInvalidToken only
      // acts on a literal "Invalid Token" body, so a 500 or a network failure is
      // swallowed here — and a swallowed failure must not look like "this
      // customer has no organisation", or callers will treat it as a denial.
      handleInvalidToken(error);
    } finally {
      organisationMembersLoading.value = false;
    }
  }

  async function verifyCustomer(payload: {
    verificationId: string;
    email?: string | null;
  }): Promise<void> {
    authLoading.value = true;

    try {
      const response = await $fetch<AuthToken & { email?: string; type?: string }>(
        `${config.public.apiBase}/api/public/customers/verify/${payload.verificationId}`,
        { method: "PUT" },
      );

      verifySuccess.value = true;
      verifyError.value = null;
      verifiedEmail.value = response.email ?? payload.email ?? null;
      verificationType.value = response.type ?? null;
      setAuthenticated(response);
      setTrackingId(null);
      authLoading.value = false;
    } catch (error: unknown) {
      verifySuccess.value = false;

      let errorMessage = "An error occurred during verification.";
      if (error && typeof error === "object" && "response" in error) {
        const resp = (error as { response?: { status?: number; _data?: unknown } }).response;
        if (resp?.status === 400) {
          errorMessage = "Verification code has expired";
        } else if (resp?._data) {
          errorMessage = typeof resp._data === "string" ? resp._data : extractErrorMessage(error);
        }
      }

      verifyError.value = errorMessage;
      token.value = null;
      authLoading.value = false;
    }
  }

  async function generateNewVerification(payload: { email: string }): Promise<void> {
    try {
      await $fetch(`${config.public.apiBase}/api/public/customers/verify/resend`, {
        method: "POST",
        body: payload,
      });

      newVerificationSuccess.value = true;
      newVerificationError.value = false;
    } catch (error: unknown) {
      newVerificationSuccess.value = false;
      newVerificationError.value = formatHttpError(
        error,
        "We couldn't resend the verification link. Please try again in a moment.",
      );
      throw error;
    }
  }

  async function forgotPassword(payload: { email: string }): Promise<void> {
    try {
      await $fetch(`${config.public.apiBase}/api/public/customers/forgotPassword`, {
        method: "POST",
        body: payload,
      });

      resetConfirmation.value = true;
    } catch (error: unknown) {
      resetConfirmation.value = false;
      throw error;
    }
  }

  async function resetCustomerPassword(payload: { password: string }): Promise<void> {
    try {
      await $fetch(`${config.public.apiBase}/api/public/customers/resetPassword`, {
        method: "PUT",
        body: payload,
      });

      resetPasswordSuccess.value = true;
      resetPasswordError.value = null;
    } catch (error: unknown) {
      resetPasswordSuccess.value = false;
      resetPasswordError.value = formatHttpError(
        error,
        "We couldn't reset your password. The link may have expired — please request a new one.",
      );
      throw error;
    }
  }

  function logout(redirect?: string): void {
    track("logout", {});
    trackReset();

    user.value = null;
    token.value = null;
    organisation.value = null;
    organisationResolved.value = false;
    organisationMembersResolved.value = false;
    customerRole.value = null;
    customerCostCentres.value = [];

    const organisationStore = useOrganisationStore();
    organisationStore.organisation = false;
    organisationStore.invoiceConfig = null;

    if (import.meta.client) {
      localStorage.removeItem("authenticated");
      localStorage.removeItem("customer");
    }

    // Recalculate cart without org pricing
    const ordersStore = useOrdersStore();
    ordersStore.recalculateCart();

    // The premium launch context names ONE organisation's order, and it lives in sessionStorage
    // — which survives a logout in the same tab. Leaving it behind meant the next person to log
    // in there inherited it: the curate page rehydrates from that key and would mount in
    // `recurate` mode against an order belonging to the organisation that just left. Nothing
    // leaks (the backend answers 401 "Not authorised" — `resolveOrderContext` checks
    // `customerCanAccessOrder`), but the screen shows a load error for an order the new customer
    // has never heard of, and it is the cross-account path behind QA's cross-org report.
    //
    // Cleared here rather than guarded at the curate page: the config is stale for every premium
    // surface that reads it, not just that one, and an identity change is the event that makes
    // it stale. The cost is that logging out mid-flow and back in as YOURSELF loses the
    // in-progress date/maker selection — accepted, because premium is auth-gated anyway, so that
    // flow was already interrupted.
    const premiumStore = usePremiumStore();
    premiumStore.clearPremiumOrderConfig();

    // The orderability verdict belongs to the customer who is leaving, and clearing the ref
    // alone would not be enough: a `checkOrderability` already in flight carries the OLD
    // customer's token and order, and its staleness check passes unless the request counter
    // moves — so it would land after the logout and write that customer's failing dishes back
    // into shared state. `clearOrderabilityFailures` does both (ref + counter bump), which is
    // why it is this and not `orderabilityFailures = []`.
    //
    // Left behind, those verdicts are read by `premium/store/[slug].vue`, `ShoppingCart` and
    // `CartSummary` — so the next person to sign in on this tab could see a blocked cart citing
    // the previous customer's dish names, until their own check happened to overwrite it.
    premiumStore.clearOrderabilityFailures();

    if (redirect) {
      navigateTo(redirect);
    }
  }

  // Staff mode's way out. It logs out a customer the staff member is logged in as too, or the rest
  // of that session would go on with no sales contact.
  function endStaffMode(): void {
    setTrackingId(null);
    if (isLoggedIn.value) logout("/search");
  }

  function setCustomerCostCentres(payload: CostCentre[]): void {
    const centres = Array.isArray(payload) ? payload : [];
    customerCostCentres.value = centres
      .map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        responsible_customer_id: item.responsible_customer_id,
        is_active: item.is_active,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }

  // --------------- Internal helpers ---------------

  function _authHeaders(): Record<string, string> {
    return {
      accept: "application/json",
      "x-access-token-customer": token.value!.token,
      "content-type": "application/json",
    };
  }

  // Survives logout on purpose, so guest checkouts and share carts made after a login-as session
  // stay attributed; only a ?loginAs= payload, a customer's own sign-in or End staff mode changes it.
  function setTrackingId(id: string | null): void {
    if (id) writeTrackingId(id);
    else clearTrackingId();
    trackingId.value = id;
    syncStaffAssisted();
  }

  function setAuthenticated(payload: AuthToken): void {
    token.value = payload;
    customerRole.value = payload.role ?? null;
    if (import.meta.client) {
      localStorage.setItem("authenticated", JSON.stringify(payload));
    }
  }

  // One-time auth hand-off from old-site beta-gate redirect.
  // Old-site appends the `authenticated` localStorage payload as
  // `#a=<urlEncodedJson>`. Adopt it into our localStorage and strip the
  // fragment so the token doesn't linger in the URL bar / history.
  function consumeAuthHandoffFragment(): void {
    if (typeof window === "undefined" || !window.location.hash) return;
    const match = window.location.hash.match(/(?:^|[#&])a=([^&]+)/);
    if (!match) return;

    try {
      const parsed = JSON.parse(decodeURIComponent(match[1])) as AuthToken;
      if (parsed && typeof parsed === "object" && typeof parsed.token === "string") {
        localStorage.setItem("authenticated", JSON.stringify(parsed));
        setTrackingId(null);
      }
    } catch {
      // ignore — fall through to localStorage / unauthenticated
    }

    const cleanedHash = window.location.hash
      .replace(/(?:^|[#&])a=[^&]+/, "")
      .replace(/^#&/, "#")
      .replace(/^#$/, "");
    history.replaceState(null, "", window.location.pathname + window.location.search + cleanedHash);
  }

  return {
    // State
    user,
    token,
    organisation,
    customerRole,
    authLoading,
    accountLoading,
    loginError,
    loginNeedsVerification,
    signUpError,
    signUpSuccess,
    verifySuccess,
    verifyError,
    verifiedEmail,
    verificationType,
    resetConfirmation,
    resetPasswordError,
    resetPasswordSuccess,
    newVerificationSuccess,
    newVerificationError,
    customerCostCentres,
    authInitialised,
    organisationResolved,
    trackingId,
    organisationMembersResolved,
    organisationMembersLoading,

    // Getters
    isLoggedIn,
    authenticated,
    isPremiumCustomer,

    // Actions
    initializeAuth,
    login,
    googleAuth,
    ssoAuth,
    lookupSsoConnection,
    register,
    convertGuestToCustomer,
    getCustomer,
    updateCustomer,
    getOrgCustomer,
    getOrganisation,
    setOrganisation,
    verifyCustomer,
    generateNewVerification,
    forgotPassword,
    resetCustomerPassword,
    logout,
    setCustomerCostCentres,
    endStaffMode,
  };
});
